import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, NotebookPen, RefreshCw, Search, UserPlus, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { staffApiService } from '../services/staffApi';
import { staffManagerApiService } from '../services/staffManagerApi';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { prefetchDiscordPlayers, useDiscordPlayer } from '../hooks/useDiscordPlayer';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PlayerProfile } from '../components/PlayerProfile';
import { StaffAvatar } from '../components/staff/StaffAvatar';
import { StaffConfidenceBadge } from '../components/staff/StaffConfidenceBadge';
import { StaffPlayerBadges } from '../components/staff/StaffPlayerBadges';
import { StaffTeamChips } from '../components/staff/StaffTeamChips';
import { StaffHonourList } from '../components/staff/StaffHonourBadge';
import { HubStat, StaffHubHero, StaffHubNav } from '../components/staff/StaffHubNav';
import { TeamColourChip } from '../components/staff/manage/TeamColourChip';
import { MembershipExpiryBadge } from '../components/staff/manage/MembershipExpiryBadge';
import { TesseractAccessNotice } from '../components/staff/manage/TesseractAccessNotice';
import { AssignToTeamModal } from '../components/staff/manage/AssignToTeamModal';
import { ScreenMarkReader } from '../components/staff/ScreenMarkReader';
import { compactPlaytime } from '../utils/staffPlayer';
import type { StaffRosterEntry, StaffRosterResponse, StaffTicketCountsResponse } from '../types/staff';
import type { StaffCapabilities, StaffDirectoryEntry, StaffDirectoryResponse, StaffDirectoryTeam, StaffNoteCountRow, StaffTeam } from '../types/staffManager';
import { getStaffPerms, STAFF_PANEL_PERMISSION } from '../utils/staffPermissions';
import { formatTenure, isMembershipActive, membershipExpiryState, staffSince } from '../utils/staffTenure';
import { formatSeconds } from '../utils/staffProfile';
import type { StaffHonour } from '../utils/staffTickets';
import { currentTicketMonth, formatTicketMonth, monthTicketTotal, personHonours, rankTicketMonth } from '../utils/staffTickets';
import { cn } from '../utils/cn';

type ProfileFilter = 'all' | 'profiled' | 'none';

/** Lower-priority roster columns drop out at narrower widths so the table fits the card instead of scrolling sideways. */
const COL_MINECRAFT = 'hidden xl:table-cell';
const COL_LAST_LOGIN = 'hidden 2xl:table-cell';
/** Fixed table layout: sized columns take their width, the three unsized ones (member, minecraft, teams) share the rest. */
const ROSTER_COLUMNS: { id: string; label: string; className: string }[] = [
  { id: 'member', label: 'Member', className: 'px-5' },
  { id: 'minecraft', label: 'Minecraft', className: `px-4 ${COL_MINECRAFT}` },
  { id: 'teams', label: 'Teams', className: 'px-4' },
  { id: 'since', label: 'Staff since', className: 'px-4 w-[124px]' },
  { id: 'login', label: 'Last login', className: `px-4 w-[150px] ${COL_LAST_LOGIN}` },
  { id: 'notes', label: 'Notes', className: 'px-4 w-[96px]' },
  { id: 'tickets', label: 'Tickets', className: 'px-4 w-[124px]' },
  { id: 'steward', label: 'Steward', className: 'px-4 w-[176px]' },
  { id: 'view', label: '', className: 'px-5 w-[96px]' },
];
type SortKey = 'name' | 'tenure' | 'login' | 'tickets' | 'built' | 'evidence';

const FILTERS: { id: ProfileFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'profiled', label: 'Profiled' },
  { id: 'none', label: 'No profile' },
];

const SORTS: { id: SortKey; label: string }[] = [
  { id: 'name', label: 'Name' },
  { id: 'tenure', label: 'Longest serving' },
  { id: 'login', label: 'Last login' },
  { id: 'tickets', label: 'Tickets this month' },
  { id: 'built', label: 'Last built' },
  { id: 'evidence', label: 'Evidence' },
];

/** One roster line: the steward entry, the Tesseract entry, or both (joined on Discord ID). */
interface RosterRow {
  key: string;
  discord_id: string | null;
  display_name: string;
  handle: string;
  steward: StaffRosterEntry | null;
  tesseract: StaffDirectoryEntry | null;
  activeTeams: StaffDirectoryTeam[];
  staff_since: number | null;
  expiringSoon: number;
  notes: { total: number; warnings: number } | null;
  /** Steward ticket counts: this month's total and rank, plus MVP / raffle honours (null when the member is not counted). */
  tickets: RosterTickets | null;
}

interface RosterTickets {
  /** null = no record for the current month (unknown, not zero). */
  thisMonth: number | null;
  rank: number | null;
  honours: StaffHonour[];
}

const normalizeUuid = (uuid: string) => uuid.toLowerCase().replace(/-/g, '');

function ticketsByDiscordId(ticketCounts: StaffTicketCountsResponse | null): Map<string, RosterTickets> {
  const map = new Map<string, RosterTickets>();
  if (!ticketCounts) return map;
  const current = currentTicketMonth(ticketCounts);
  const ranked = new Map(current ? rankTicketMonth(ticketCounts, current.month).map((r) => [r.person.discord_id, r]) : []);
  for (const person of ticketCounts.staff) {
    const row = ranked.get(person.discord_id);
    map.set(person.discord_id, { thisMonth: row ? row.standing.tickets : null, rank: row?.rank ?? null, honours: personHonours(person) });
  }
  return map;
}

function mergeRoster(
  roster: StaffRosterResponse | null,
  directory: StaffDirectoryResponse | null,
  noteCounts: StaffNoteCountRow[] | null,
  ticketCounts: StaffTicketCountsResponse | null,
): RosterRow[] {
  const now = Date.now();
  const tickets = ticketsByDiscordId(ticketCounts);
  const notesByKey = new Map<string, { total: number; warnings: number }>();
  for (const c of noteCounts ?? []) {
    const bump = (k: string) => {
      const prev = notesByKey.get(k) ?? { total: 0, warnings: 0 };
      notesByKey.set(k, { total: prev.total + c.total, warnings: prev.warnings + c.warnings });
    };
    if (c.subject_discord_id) bump(`d:${c.subject_discord_id}`);
    if (c.subject_uuid) bump(`u:${normalizeUuid(c.subject_uuid)}`);
    if (c.subject_user_id) bump(`t:${c.subject_user_id}`);
  }
  const notesFor = (discordId: string | null, uuid: string | null, userId: string | null) => {
    const hit = (discordId && notesByKey.get(`d:${discordId}`)) || (uuid && notesByKey.get(`u:${normalizeUuid(uuid)}`)) || (userId && notesByKey.get(`t:${userId}`)) || null;
    return noteCounts ? hit ?? { total: 0, warnings: 0 } : null;
  };

  const rows = new Map<string, RosterRow>();
  for (const s of roster?.staff ?? []) {
    rows.set(`d:${s.discord_id}`, {
      key: `d:${s.discord_id}`,
      discord_id: s.discord_id,
      display_name: s.display_name,
      handle: s.username,
      steward: s,
      tesseract: null,
      activeTeams: [],
      staff_since: null,
      expiringSoon: 0,
      notes: notesFor(s.discord_id, null, null),
      tickets: tickets.get(s.discord_id) ?? null,
    });
  }
  for (const e of directory?.staff ?? []) {
    const activeTeams = e.teams.filter((t) => isMembershipActive(t, now));
    const expiringSoon = activeTeams.filter((t) => membershipExpiryState(t.expires_at, now).state === 'expiring').length;
    const existing = e.discord_id ? rows.get(`d:${e.discord_id}`) : undefined;
    if (existing) {
      existing.tesseract = e;
      existing.activeTeams = activeTeams;
      existing.staff_since = staffSince(e.teams, now);
      existing.expiringSoon = expiringSoon;
      existing.notes = notesFor(e.discord_id, e.minecraft_uuid, e.user_id);
      continue;
    }
    const key = e.discord_id ? `d:${e.discord_id}` : `t:${e.user_id}`;
    rows.set(key, {
      key,
      discord_id: e.discord_id,
      display_name: e.minecraft_username ?? e.discord_username ?? e.username,
      handle: e.discord_username ?? e.username,
      steward: null,
      tesseract: e,
      activeTeams,
      staff_since: staffSince(e.teams, now),
      expiringSoon,
      notes: notesFor(e.discord_id, e.minecraft_uuid, e.user_id),
      tickets: e.discord_id ? tickets.get(e.discord_id) ?? null : null,
    });
  }
  return Array.from(rows.values());
}

/** Linked Minecraft account for a roster row: name link, flags, last server, playtime. */
const MinecraftCell = ({ discordId, fallbackUuid, compact = false }: { discordId: string | null; fallbackUuid: string | null; compact?: boolean }) => {
  const { uuid: linkedUuid, username, stats, loading, notFound } = useDiscordPlayer(discordId ?? '');
  const uuid = linkedUuid ?? fallbackUuid;
  if (!discordId && !uuid) return <span className="text-xs text-glass-500 italic whitespace-nowrap">Not linked</span>;
  if (!uuid && loading) return <span className="text-xs text-glass-500 animate-pulse">Resolving…</span>;
  if (!uuid || (notFound && !fallbackUuid)) return <span className="text-xs text-glass-500 italic whitespace-nowrap">Not linked</span>;
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5">
        <PlayerProfile uuid={uuid} name={username ?? undefined} showUuid={false} inline />
        <StaffPlayerBadges stats={stats} />
      </div>
      {stats && (
        <div className="text-[11px] font-mono text-glass-500 mt-0.5 truncate">
          {stats.lastjoinedservername ? `last on ${stats.lastjoinedservername}` : 'no server seen'}
          {!compact && ` · ${compactPlaytime(stats.playtime)} played`}
        </div>
      )}
    </div>
  );
};

/** Team chips: Tesseract teams with colours when the directory is available, else the public roster chips. */
const TeamsCell = ({ row }: { row: RosterRow }) => {
  const { uuid } = useDiscordPlayer(row.discord_id ?? '');
  if (row.tesseract) {
    if (row.activeTeams.length === 0) return <span className="text-xs text-glass-500">—</span>;
    return (
      <span className="inline-flex flex-wrap gap-1">
        {row.activeTeams.map((t) => (
          <span key={t.id} className="inline-flex flex-wrap items-center gap-1">
            <TeamColourChip team={t} title={t.assigned_at ? `Joined ${formatTenure(t.assigned_at)} ago` : undefined} />
            <MembershipExpiryBadge expiresAt={t.expires_at} />
          </span>
        ))}
      </span>
    );
  }
  return <StaffTeamChips uuid={uuid} emptyLabel="—" />;
};

const NotesCell = ({ notes }: { notes: RosterRow['notes'] }) => {
  if (!notes) return null;
  if (notes.total === 0) return <span className="text-xs text-glass-500">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-semibold border bg-amber-500/15 text-amber-300 border-amber-500/30 whitespace-nowrap">
        <NotebookPen className="w-3 h-3" /> {notes.total}
      </span>
      {notes.warnings > 0 && <span className="text-[10px] font-semibold text-yellow-300 whitespace-nowrap">{notes.warnings} warn</span>}
    </span>
  );
};

/** This month's closed tickets with rank, and the member's MVP / raffle pills (newest two). */
const TicketsCell = ({ tickets }: { tickets: RosterTickets | null }) => {
  if (!tickets) return <span className="text-xs text-glass-500">—</span>;
  return (
    <div className="min-w-0">
      <div className="text-sm text-white font-semibold tabular-nums">
        {tickets.thisMonth ?? '—'}
        {tickets.rank !== null && <span className="ml-1.5 text-[11px] font-mono font-normal text-glass-500">#{tickets.rank}</span>}
      </div>
      {tickets.honours.length > 0 ? <StaffHonourList honours={tickets.honours} max={2} compact className="mt-1" /> : <div className="text-[11px] text-glass-500">this month</div>}
    </div>
  );
};

/** Staff Manager hub — merged roster (steward profiles + Tesseract directory) with tenure, teams, tickets and notes. */
export const StaffManagerView = () => {
  useDocumentTitle('Staff Manager');
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getStaffPerms(identity, isDev), [identity, isDev]);

  const [roster, setRoster] = useState<StaffRosterResponse | null>(() => staffApiService.getCachedRoster());
  const [directory, setDirectory] = useState<StaffDirectoryResponse | null>(() => staffManagerApiService.peek<StaffDirectoryResponse>('directory'));
  const [capabilities, setCapabilities] = useState<StaffCapabilities | null>(() => staffManagerApiService.peek<StaffCapabilities>('capabilities'));
  const [noteCounts, setNoteCounts] = useState<StaffNoteCountRow[] | null>(null);
  const [ticketCounts, setTicketCounts] = useState<StaffTicketCountsResponse | null>(() => staffApiService.getCachedTicketCounts());
  const [teams, setTeams] = useState<StaffTeam[]>([]);
  const [loading, setLoading] = useState(roster === null && directory === null);
  const [refreshing, setRefreshing] = useState(false);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [filter, setFilter] = useState<ProfileFilter>('all');
  const [teamFilter, setTeamFilter] = useState<string>('');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>('name');
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(
    async (force = false) => {
      if (force) setRefreshing(true);
      setRosterError(null);
      setDirectoryError(null);
      const [rosterRes, dirRes, capRes, notesRes, teamsRes, ticketsRes] = await Promise.allSettled([
        staffApiService.getRoster({ force }),
        staffManagerApiService.getDirectory({ force }),
        staffManagerApiService.getCapabilities({ force }),
        perms.canReadNotes ? staffManagerApiService.getNoteCounts({ force }) : Promise.resolve(null),
        perms.canManage ? staffManagerApiService.getTeams({ force }) : Promise.resolve(null),
        // Best effort: the Tickets column and stat simply stay off when the bot is unreachable.
        staffApiService.getTicketCounts({ force }).catch(() => null),
      ]);
      if (rosterRes.status === 'fulfilled') {
        setRoster(rosterRes.value);
        void prefetchDiscordPlayers(rosterRes.value.staff.map((s) => s.discord_id));
      } else {
        setRosterError(rosterRes.reason instanceof Error ? rosterRes.reason.message : 'Failed to load the staff roster');
      }
      if (dirRes.status === 'fulfilled') {
        setDirectory(dirRes.value);
        void prefetchDiscordPlayers(dirRes.value.staff.map((s) => s.discord_id).filter((d): d is string => !!d));
      } else {
        setDirectoryError(dirRes.reason instanceof Error ? dirRes.reason.message : 'Failed to load the Tesseract directory');
      }
      if (capRes.status === 'fulfilled') setCapabilities(capRes.value);
      if (notesRes.status === 'fulfilled') setNoteCounts(notesRes.value);
      if (teamsRes.status === 'fulfilled' && teamsRes.value) setTeams(teamsRes.value.teams);
      if (ticketsRes.status === 'fulfilled' && ticketsRes.value) setTicketCounts(ticketsRes.value);
      setLoading(false);
      setRefreshing(false);
    },
    [perms.canManage, perms.canReadNotes],
  );

  useEffect(() => {
    if (perms.canView) void load();
  }, [perms.canView, load]);

  const allRows = useMemo(() => mergeRoster(roster, directory, noteCounts, ticketCounts), [roster, directory, noteCounts, ticketCounts]);
  const ticketStats = useMemo(() => {
    const current = ticketCounts ? currentTicketMonth(ticketCounts) : null;
    if (!ticketCounts || !current) return null;
    const leader = rankTicketMonth(ticketCounts, current.month).find((r) => r.rank === 1) ?? null;
    return { month: current.month, total: monthTicketTotal(ticketCounts, current.month), leader };
  }, [ticketCounts]);
  const teamOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of allRows) for (const t of r.activeTeams) map.set(t.id, t.display_name);
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allRows]);
  const maxEvidence = useMemo(() => Math.max(1, ...allRows.map((r) => r.steward?.evidence_count ?? 0)), [allRows]);

  const rows = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const list = allRows.filter((r) => {
      if (filter === 'profiled' && !r.steward?.has_profile) return false;
      if (filter === 'none' && r.steward?.has_profile) return false;
      if (teamFilter && !r.activeTeams.some((t) => t.id === teamFilter)) return false;
      if (expiringOnly && r.expiringSoon === 0) return false;
      if (!q) return true;
      const hay = [r.display_name, r.handle, r.discord_id ?? '', r.tesseract?.email ?? '', r.tesseract?.minecraft_username ?? '', r.tesseract?.username ?? ''].join(' ').toLowerCase();
      return hay.includes(q);
    });
    return list.sort((a, b) => {
      if (sort === 'tenure') return (a.staff_since ?? Infinity) - (b.staff_since ?? Infinity) || a.display_name.localeCompare(b.display_name);
      if (sort === 'login') return (b.tesseract?.last_login ?? 0) - (a.tesseract?.last_login ?? 0) || a.display_name.localeCompare(b.display_name);
      if (sort === 'tickets') return (b.tickets?.thisMonth ?? -1) - (a.tickets?.thisMonth ?? -1) || a.display_name.localeCompare(b.display_name);
      if (sort === 'built') return (b.steward?.built_at ?? 0) - (a.steward?.built_at ?? 0) || a.display_name.localeCompare(b.display_name);
      if (sort === 'evidence') return (b.steward?.evidence_count ?? 0) - (a.steward?.evidence_count ?? 0) || a.display_name.localeCompare(b.display_name);
      return a.display_name.localeCompare(b.display_name);
    });
  }, [allRows, debouncedSearch, filter, teamFilter, expiringOnly, sort]);

  if (!perms.canView) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-glass-300">You don't have permission to view the Staff Manager.</p>
        <p className="text-glass-500 text-sm mt-1">
          Ask an admin for the <code>{STAFF_PANEL_PERMISSION}</code> permission.
        </p>
      </div>
    );
  }

  const nowSeconds = Date.now() / 1000;
  const renderBuilt = (entry: StaffRosterEntry | null) =>
    entry?.built_at ? `${formatSeconds((roster?.generated_at ?? nowSeconds) - entry.built_at)} ago` : '—';
  const linkFor = (row: RosterRow) => (row.discord_id ? `/staff/${row.discord_id}` : row.tesseract?.minecraft_uuid ? `/player/${row.tesseract.minecraft_uuid}` : null);
  const canAddMember = perms.canManage && !!capabilities?.tesseract.connected && capabilities.tesseract.can_edit_teams && teams.length > 0;
  const expiringTotal = allRows.reduce((n, r) => n + r.expiringSoon, 0);
  const warningsTotal = noteCounts ? noteCounts.reduce((n, c) => n + c.warnings, 0) : null;
  const hasAnyData = roster !== null || directory !== null;

  return (
    <div className="space-y-6">
      <StaffHubHero
        title="Staff Manager"
        blurb="Everyone on the staff roster in one place: Tesseract identity and tenure, team membership, steward observations, and Staff Manager Notes."
        nav={<StaffHubNav perms={perms} />}
        actions={
          <>
            {canAddMember && (
              <button type="button" onClick={() => setAssignOpen(true)} className="glass-button inline-flex items-center gap-2 text-sm">
                <UserPlus className="w-4 h-4" />
                Add member
              </button>
            )}
            <button type="button" onClick={() => void load(true)} className="glass-button-secondary inline-flex items-center gap-2 text-sm" disabled={refreshing || loading}>
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
              Refresh
            </button>
          </>
        }
        stats={
          hasAnyData ? (
            <>
              <HubStat label="Staff" value={allRows.length} sub={directory ? 'Tesseract ∪ steward roster' : 'steward roster'} />
              <HubStat label="In Tesseract" value={directory?.staff.length ?? 0} sub={directory ? 'active team memberships' : 'directory unavailable'} tone="text-blue-300" />
              <HubStat label="Profiled" value={roster?.profiled_count ?? 0} sub="steward profiles built" tone="text-green-300" />
              {ticketStats && (
                <HubStat
                  label="Tickets this month"
                  value={ticketStats.total}
                  sub={ticketStats.leader ? `leading: ${ticketStats.leader.person.name} · ${ticketStats.leader.standing.tickets}` : formatTicketMonth(ticketStats.month)}
                  tone="text-purple-300"
                />
              )}
              <HubStat label="Expiring soon" value={expiringTotal} sub="memberships within 14 days" tone={expiringTotal > 0 ? 'text-amber-300' : 'text-glass-400'} />
              {warningsTotal !== null && <HubStat label="Staff warnings" value={warningsTotal} sub="active Staff Manager warnings" tone={warningsTotal > 0 ? 'text-yellow-300' : 'text-glass-400'} />}
            </>
          ) : undefined
        }
      />

      {directoryError && !loading && <TesseractAccessNotice capabilities={capabilities} needs="role.view" error={directoryError} />}

      {rosterError && !directory && !loading && (
        <div className="glass-card p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Failed to load the roster</h2>
          <p className="text-glass-400">{rosterError}</p>
          <button onClick={() => void load(true)} className="glass-button mt-4">
            Retry
          </button>
        </div>
      )}
      {rosterError && directory && !loading && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-amber-300" /> Steward roster unavailable: {rosterError}. Showing Tesseract data only.
        </div>
      )}

      {loading && (
        <div className="glass-card p-6">
          <SkeletonLoader className="h-10 w-full mb-4" />
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonLoader key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      )}

      {hasAnyData && !loading && (
        <div className="glass-card relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-400/50 to-transparent" aria-hidden="true" />
          <div className="p-4 md:p-5 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                <input
                  className="glass-input w-full pl-10"
                  placeholder="Search by name, handle, email, Discord ID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search staff"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border border-glass-200/20 p-0.5" role="group" aria-label="Profile filter">
                  {FILTERS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setFilter(f.id)}
                      aria-pressed={filter === f.id}
                      className={cn('h-8 px-3 rounded-md text-xs font-semibold transition-colors touch-manipulation', filter === f.id ? 'bg-accent-500/20 text-accent-300' : 'text-glass-400 hover:text-white')}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                {teamOptions.length > 0 && (
                  <select className="glass-select h-9 py-0 text-sm" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} aria-label="Filter by team">
                    <option value="">All teams</option>
                    {teamOptions.map(([id, label]) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </select>
                )}
                {directory && (
                  <button
                    type="button"
                    onClick={() => setExpiringOnly((v) => !v)}
                    aria-pressed={expiringOnly}
                    className={cn('h-8 px-3 rounded-md text-xs font-semibold border transition-colors touch-manipulation', expiringOnly ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'border-glass-200/20 text-glass-400 hover:text-white')}
                  >
                    Expiring soon{expiringTotal > 0 ? ` · ${expiringTotal}` : ''}
                  </button>
                )}
                <select className="glass-select h-9 py-0 text-sm" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort by">
                  {SORTS.map((s) => (
                    <option key={s.id} value={s.id}>
                      Sort: {s.label}
                    </option>
                  ))}
                </select>
                <span className="text-xs font-mono text-glass-500 tabular-nums whitespace-nowrap">
                  {rows.length} / {allRows.length}
                </span>
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="p-10 text-center text-glass-400">
                <Users className="w-8 h-8 mx-auto mb-2 text-glass-500" />
                No staff match these filters.
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden lg:block overflow-x-auto -mx-4 md:-mx-5">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="border-b border-glass-200/15">
                        {ROSTER_COLUMNS.filter((c) => (c.id !== 'notes' || noteCounts) && (c.id !== 'tickets' || ticketCounts)).map((c) => (
                          <th key={c.id} className={cn('text-left text-[11px] uppercase tracking-[0.14em] text-glass-500 font-semibold py-2.5 whitespace-nowrap', c.className)}>
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const href = linkFor(row);
                        return (
                          <tr
                            key={row.key}
                            onClick={() => href && navigate(href)}
                            className={cn('group border-b border-glass-200/10 transition-colors', href ? 'hover:bg-accent-500/5 cursor-pointer' : '')}
                          >
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3 min-w-0">
                                {row.discord_id ? (
                                  <StaffAvatar discordId={row.discord_id} name={row.display_name} />
                                ) : (
                                  <span className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-500/30 to-accent-700/20 inline-flex items-center justify-center font-bold text-accent-200" aria-hidden="true">
                                    {(row.display_name[0] || '?').toUpperCase()}
                                  </span>
                                )}
                                <div className="min-w-0">
                                  <div className="text-white font-semibold truncate group-hover:text-accent-300 transition-colors">{row.display_name}</div>
                                  <div className="text-[11px] font-mono text-glass-400 truncate">
                                    @{row.handle}
                                    {row.discord_id && <span className="text-glass-500 hidden 2xl:inline"> · {row.discord_id}</span>}
                                    {!row.steward && <span className="text-glass-500"> · Tesseract only</span>}
                                    {!row.tesseract && directory && <span className="text-amber-300/80"> · not in Tesseract</span>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className={cn('px-4 py-3', COL_MINECRAFT)} onClick={(e) => e.stopPropagation()}>
                              <MinecraftCell discordId={row.discord_id} fallbackUuid={row.tesseract?.minecraft_uuid ?? null} />
                            </td>
                            <td className="px-4 py-3">
                              <TeamsCell row={row} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {row.staff_since ? (
                                <>
                                  <div className="text-sm text-white font-semibold tabular-nums">{formatTenure(row.staff_since)}</div>
                                  <div className="text-[11px] text-glass-500">{formatDistanceToNow(new Date(row.staff_since), { addSuffix: true })}</div>
                                </>
                              ) : (
                                <span className="text-xs text-glass-500">—</span>
                              )}
                            </td>
                            <td className={cn('px-4 py-3 text-sm text-glass-300', COL_LAST_LOGIN)}>
                              {row.tesseract?.last_login ? formatDistanceToNow(new Date(row.tesseract.last_login), { addSuffix: true }) : <span className="text-xs text-glass-500">—</span>}
                            </td>
                            {noteCounts && (
                              <td className="px-4 py-3">
                                <NotesCell notes={row.notes} />
                              </td>
                            )}
                            {ticketCounts && (
                              <td className="px-4 py-3">
                                <TicketsCell tickets={row.tickets} />
                              </td>
                            )}
                            <td className="px-4 py-3">
                              {row.steward?.has_profile ? (
                                <div className="space-y-1">
                                  <StaffConfidenceBadge confidence={row.steward.confidence} compact />
                                  <div className="text-[11px] text-glass-500">
                                    built {renderBuilt(row.steward)} · {row.steward.evidence_count} obs · {Math.round((row.steward.evidence_count / maxEvidence) * 100)}%
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs text-glass-500 italic whitespace-nowrap">
                                  {row.steward ? `No profile yet${row.steward.evidence_count > 0 ? ` · ${row.steward.evidence_count} obs` : ''}` : '—'}
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3 text-right">
                              {href && (
                                <Link to={href} onClick={(e) => e.stopPropagation()} className="glass-button-sm inline-flex items-center gap-1 whitespace-nowrap">
                                  View
                                  <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                                </Link>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="lg:hidden space-y-2.5">
                  {rows.map((row) => {
                    const href = linkFor(row);
                    const inner = (
                      <>
                        {row.discord_id ? (
                          <StaffAvatar discordId={row.discord_id} name={row.display_name} />
                        ) : (
                          <span className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-500/30 to-accent-700/20 inline-flex items-center justify-center font-bold text-accent-200" aria-hidden="true">
                            {(row.display_name[0] || '?').toUpperCase()}
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-white truncate">{row.display_name}</span>
                            {row.steward?.has_profile ? <StaffConfidenceBadge confidence={row.steward.confidence} compact /> : <NotesCell notes={row.notes} />}
                          </div>
                          <p className="text-[11px] font-mono text-glass-400 mt-0.5 truncate">
                            @{row.handle}
                            {row.staff_since && ` · staff ${formatTenure(row.staff_since)}`}
                            {row.tickets?.thisMonth !== null && row.tickets?.thisMonth !== undefined && ` · ${row.tickets.thisMonth} tickets`}
                            {row.tesseract?.last_login && ` · seen ${formatDistanceToNow(new Date(row.tesseract.last_login), { addSuffix: true })}`}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1" onClick={(e) => e.stopPropagation()}>
                            <MinecraftCell discordId={row.discord_id} fallbackUuid={row.tesseract?.minecraft_uuid ?? null} compact />
                            <TeamsCell row={row} />
                          </div>
                        </div>
                        {href && <ArrowRight className="w-4 h-4 text-glass-400 flex-shrink-0" />}
                      </>
                    );
                    // A div, not a Link: the card contains its own links (player profile, team chips) and anchors cannot nest.
                    return href ? (
                      <div
                        key={row.key}
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate(href)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(href);
                          }
                        }}
                        className="glass-card p-3 flex items-center gap-3 touch-manipulation cursor-pointer active:bg-glass-200/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60"
                      >
                        {inner}
                      </div>
                    ) : (
                      <div key={row.key} className="glass-card p-3 flex items-center gap-3">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Leak tracing: the panel marks every screen with the account looking at it. */}
      {perms.canManage && <ScreenMarkReader />}

      <AssignToTeamModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        teams={teams}
        onSuccess={() => {
          void load(true);
        }}
      />
    </div>
  );
};
